<script setup lang="ts">
// MemberRolePicker — v3's `activeRoles` card list (`UserPermissionModal.vue:135-181`) as a real
// radio group.
//
// It holds NO matrix of its own. `assignableRoles(scope)` is the catalog's single encoding of the
// rule (`owner` never assignable; dashboard scope offers exactly `viewer` + `viewer_plus`;
// `viewer_plus` filtered out everywhere else), so the "which roles at which scope" question has
// one answer in the codebase rather than one per screen. v3 re-derived it here from `r.dash`,
// `r.only_dash` and `r.value !== 'owner'` flags that lived on a `ref([...])` literal in the parent.
//
// The description copy is v3's `roleDescriptions` map (`:309-314`) plus its one contextual
// override: at dashboard scope `viewer` reads "Can view dashboards." instead of the account-wide
// sentence (`:161-163`). Both are i18n keys now.
import { computed } from 'vue'

import type { MemberRole, MemberScope } from '../../models/member.types'
import { assignableRoles } from '../../models/memberCatalog'

import { useI18n } from '@/shared/i18n'
import { Icon, Radio } from '@/shared/ui'

const model = defineModel<MemberRole | null>({ default: null })

const props = withDefaults(
  defineProps<{
    scope: MemberScope
    disabled?: boolean
    /**
     * Per-role i18n key overrides (ORP-21470). Absent ⇒ the web pane's own sentences. MCP-only
     * passes its three, because the shipped copy talks about DASHBOARDS ("Can view dashboards,
     * reports and recommendations") and that product has none — the AC there is that nobody has
     * to guess the Editor/Admin difference, which wrong copy defeats more thoroughly than none.
     */
    descriptionKeys?: Partial<Record<MemberRole, string>> | undefined
  }>(),
  { disabled: false },
)

const { t } = useI18n()

/** Public help article, unchanged from v3 (`:171`). A URL, not user-facing copy. */
const ROLES_HELP_URL =
  'https://intercom.help/orphex/en/articles/13868685-understanding-user-roles-and-permissions'

const DESCRIPTION_KEY: Readonly<Record<MemberRole, string>> = {
  viewer: 'viewer',
  viewer_plus: 'viewerPlus',
  editor: 'editor',
  admin: 'admin',
  owner: 'owner',
}

/** An explicit override wins; otherwise v3's only scope-sensitive sentence applies (a
 *  dashboard-scoped Read Only sees dashboards, not the account's reports and recommendations). */
function descriptionKey(role: MemberRole): string {
  const override = props.descriptionKeys?.[role]
  if (override !== undefined) return override
  return props.scope === 'dashboard' && role === 'viewer'
    ? 'workspaceSettings.permissions.invite.roleDescription.viewerDashboard'
    : `workspaceSettings.permissions.invite.roleDescription.${DESCRIPTION_KEY[role]}`
}

const options = computed(() =>
  assignableRoles(props.scope).map((entry) => ({
    role: entry.role,
    label: t(entry.labelKey),
    description: t(descriptionKey(entry.role)),
  })),
)
</script>

<template>
  <fieldset
    class="role-picker flex flex-col gap-xs min-w-0 m-0 p-0 border-0 font-sans"
    :disabled="props.disabled"
    data-testid="member-role-picker"
  >
    <legend class="role-picker__legend mb-2xs text-fg text-[0.8125rem] font-semibold">
      {{ t('workspaceSettings.permissions.invite.roleLabel') }}
    </legend>

    <div
      v-for="option in options"
      :key="option.role"
      class="role-picker__card p-sm border border-border rounded-md bg-surface"
      :class="{ 'role-picker__card--selected': model === option.role }"
      :data-role="option.role"
    >
      <Radio
        v-model="model"
        :label="option.label"
        :value="option.role"
        name="member-role"
        :disabled="props.disabled"
      />
      <p class="role-picker__description mt-2xs mr-0 mb-0 ml-6.5 text-muted text-[0.75rem]">
        {{ option.description }}
      </p>
    </div>

    <a
      class="role-picker__help inline-flex items-center self-start gap-2xs mt-2xs text-muted text-[0.75rem] underline [&:hover]:text-primary"
      :href="ROLES_HELP_URL"
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon name="circle-question" :size="12" />
      {{ t('workspaceSettings.permissions.invite.rolesHelp') }}
    </a>
  </fieldset>
</template>

<style scoped>
.role-picker .role-picker__card--selected {
  border-color: var(--ui-color-primary);
  background-color: var(--ui-color-surface-sunken);
}
</style>
